import { z } from "zod";
import { timestampFieldsSchema } from "@/schemas/common";

// =============================================================================
// USER PROFILE SCHEMAS
// =============================================================================

/**
 * Credit score range enum
 */
export const creditScoreRangeSchema = z.enum([
	"excellent",
	"good",
	"fair",
	"poor",
	"very_poor",
]);
export type CreditScoreRange = z.infer<typeof creditScoreRangeSchema>;

/**
 * Pre-approval status enum
 */
export const preApprovalStatusSchema = z.enum([
	"not_started",
	"in_progress",
	"pre_approved",
	"approved",
]);
export type PreApprovalStatus = z.infer<typeof preApprovalStatusSchema>;

/**
 * User profile base schema - core fields
 */
export const userProfileBaseSchema = z.object({
	avatar_url: z.string().optional().nullable(),
	primer_nombre: z.string().min(1, "El primer nombre es requerido").optional(),
	apellido: z.string().optional(),
	telefono: z.string().optional(),
	biografia: z
		.string()
		.max(500, "La biografía no puede exceder 500 caracteres")
		.optional(),
	ocupacion: z.string().optional(),
	fecha_nacimiento: z.string().optional(), // ISO date string
	// Financial profile fields
	credit_score_range: creditScoreRangeSchema.optional(),
	debt_to_income_ratio: z.number().min(0).max(100).optional(),
	first_time_homebuyer: z.boolean().default(false),
	pre_approval_status: preApprovalStatusSchema.default("not_started"),
});

export type UserProfileBase = z.infer<typeof userProfileBaseSchema>;

/**
 * User profile response from backend API
 */
export const userProfileResponseSchema = userProfileBaseSchema
	.merge(timestampFieldsSchema)
	.extend({
		id: z.string().uuid(),
		correo: z.string().email(),
		es_admin: z.boolean().default(false),
	});

export type UserProfileResponse = z.infer<typeof userProfileResponseSchema>;

/**
 * Combined user profile (Supabase Auth + Backend)
 * Merges Supabase user metadata with backend profile data
 */
export const combinedUserProfileSchema = z.object({
	// From Supabase Auth
	id: z.string().uuid(),
	email: z.string().email().optional(),
	full_name: z.string().optional(),
	avatar_url: z.string().optional().nullable(),

	// From Backend - Personal Info
	primer_nombre: z.string().optional(),
	apellido: z.string().optional(),
	telefono: z.string().optional(),
	biografia: z.string().optional(),
	ocupacion: z.string().optional(),
	fecha_nacimiento: z.string().optional(),

	// From Backend - Financial Profile
	credit_score_range: creditScoreRangeSchema.optional(),
	debt_to_income_ratio: z.number().optional(),
	first_time_homebuyer: z.boolean().default(false),
	pre_approval_status: preApprovalStatusSchema.default("not_started"),

	// System fields
	es_admin: z.boolean().default(false),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
});

export type CombinedUserProfile = z.infer<typeof combinedUserProfileSchema>;

/**
 * Profile form input schema (for editing)
 */
export const profileFormInputSchema = z.object({
	avatar_url: z.string().optional().nullable(),
	// Personal Information
	primer_nombre: z.string().min(1, "El primer nombre es requerido"),
	apellido: z.string().optional(),
	telefono: z.string().optional(),
	biografia: z
		.string()
		.max(500, "La biografía no puede exceder 500 caracteres")
		.optional(),
	ocupacion: z.string().optional(),
	fecha_nacimiento: z.string().optional(),

	// Financial Profile
	credit_score_range: creditScoreRangeSchema.optional(),
	debt_to_income_ratio: z.number().min(0).max(100).optional(),
	first_time_homebuyer: z.boolean().optional(),
	pre_approval_status: preApprovalStatusSchema.optional(),
});

export type ProfileFormInput = z.infer<typeof profileFormInputSchema>;

/**
 * Profile form data (for submission to API)
 */
export type ProfileFormData = ProfileFormInput;

/**
 * API validation error structure
 */
export interface ApiValidationError {
	loc: (string | number)[];
	msg: string;
	type: string;
	ctx?: {
		error?: string;
	};
}

/**
 * Map API error location to form field path
 */
export function mapApiErrorToFieldPath(
	loc: (string | number)[],
): keyof ProfileFormInput | null {
	if (loc.length === 0) return null;

	// Handle FastAPI body validation errors
	// loc format: ["body", "field_name"] or ["field_name"]
	const fieldName = loc[loc.length - 1];

	const fieldMap: Record<string, keyof ProfileFormInput> = {
		avatar_url: "avatar_url",
		primer_nombre: "primer_nombre",
		apellido: "apellido",
		telefono: "telefono",
		biografia: "biografia",
		ocupacion: "ocupacion",
		fecha_nacimiento: "fecha_nacimiento",
		credit_score_range: "credit_score_range",
		debt_to_income_ratio: "debt_to_income_ratio",
		first_time_homebuyer: "first_time_homebuyer",
		pre_approval_status: "pre_approval_status",
	};

	return fieldMap[String(fieldName)] || null;
}
